import { useEffect, useState, useCallback, useRef } from 'react';

interface ActivityTimeoutOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
  onTimeout?: () => void;
  enabled?: boolean;
}

interface ActivityTimeoutReturn {
  showWarning: boolean;
  timeRemaining: number | null;
  extendSession: () => void;
}

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'] as const;

export function useActivityTimeout({
  timeoutMinutes = 30,
  warningMinutes = 5,
  onTimeout,
  enabled = true,
}: ActivityTimeoutOptions = {}): ActivityTimeoutReturn {
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const hasTriggeredTimeout = useRef(false);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
    setShowWarning(false);
    hasTriggeredTimeout.current = false;
  }, []);

  // Extend session (reset timer)
  const extendSession = useCallback(() => {
    updateActivity();
  }, [updateActivity]);

  // Track activity events
  useEffect(() => {
    if (!enabled) return;

    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [enabled, updateActivity]);

  // Check timeout every second
  useEffect(() => {
    if (!enabled) return;

    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = warningMinutes * 60 * 1000;

    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivity;
      const remaining = timeoutMs - elapsed;

      setTimeRemaining(Math.max(0, remaining));

      // Show warning when time remaining is less than warning threshold
      if (remaining <= warningMs && remaining > 0) {
        setShowWarning(true);
      }

      // Trigger timeout callback when time expires
      if (remaining <= 0 && !hasTriggeredTimeout.current) {
        hasTriggeredTimeout.current = true;
        onTimeout?.();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lastActivity, timeoutMinutes, warningMinutes, enabled, onTimeout]);

  return { showWarning, timeRemaining, extendSession };
}
